import {
  Component,
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  type MouseEventHandler,
  type ReactNode
} from "react";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  CircleHelp,
  Lightbulb,
  Pause,
  Play,
  RotateCcw
} from "lucide-react";

import type { InputMethod } from "@/game/input";
import {
  LiveCaption,
  useAccessibleActivation
} from "@/platform/accessibility";
import {
  AudioReplayButton,
  IconButton,
  PrimaryCommandButton
} from "@/ui";

import type {
  ActivityHostFeedback,
  ActivityHostProps,
  ActivityHostRenderContext
} from "./contracts";
import {
  useActivityHost,
  type ActivityHostController
} from "./useActivityHost";
import "./ActivityHost.css";

interface RenderBoundaryProps {
  readonly children: ReactNode;
  readonly onError: () => void;
}

interface RenderBoundaryState {
  readonly failed: boolean;
}

class ActivityRenderBoundary extends Component<
  RenderBoundaryProps,
  RenderBoundaryState
> {
  state: RenderBoundaryState = { failed: false };

  static getDerivedStateFromError(): RenderBoundaryState {
    return { failed: true };
  }

  componentDidCatch(): void {
    this.props.onError();
  }

  render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

interface ActivityRendererProps<TAction, TRenderModel> {
  readonly context: ActivityHostRenderContext<TAction, TRenderModel>;
  readonly renderActivity: (
    context: ActivityHostRenderContext<TAction, TRenderModel>
  ) => ReactNode;
}

function ActivityRenderer<TAction, TRenderModel>({
  context,
  renderActivity
}: ActivityRendererProps<TAction, TRenderModel>) {
  return <Fragment>{renderActivity(context)}</Fragment>;
}

function getFeedbackMessage(feedback: ActivityHostFeedback): string {
  const explicitMessage = feedback.message?.trim();

  if (explicitMessage) {
    return explicitMessage;
  }

  if (feedback.kind === "retry") {
    return "差一点点。换个想法，我们再试试。";
  }

  switch (feedback.stage) {
    case "gentle-hint":
      return "点点把小提示轻轻点亮了。看看它，再试试。";
    case "reduced-choices":
      return "点点收起了几个选项。看看剩下的，慢慢找。";
    case "replay-demonstration":
      return "跟着点点再看一遍，答案会更清楚。";
  }
}

function formatCorrectRate(correctRate: number): string {
  if (!Number.isFinite(correctRate)) {
    return "0%";
  }

  const percent = Math.round(Math.min(1, Math.max(0, correctRate)) * 100);
  return percent + "%";
}

interface FeedbackNoticeProps {
  readonly feedback: ActivityHostFeedback | null;
  readonly onRetry: MouseEventHandler<HTMLButtonElement>;
  readonly suppressed: boolean;
}

function FeedbackNotice({
  feedback,
  onRetry,
  suppressed
}: FeedbackNoticeProps) {
  if (feedback === null) {
    return (
      <div
        aria-hidden="true"
        className="activity-host__feedback-slot"
        data-feedback="none"
        inert={suppressed ? true : undefined}
      />
    );
  }

  const Icon = feedback.kind === "hint" ? Lightbulb : CircleHelp;

  return (
    <div
      aria-atomic="true"
      aria-hidden={suppressed || undefined}
      aria-live="polite"
      className="activity-host__feedback-slot activity-host__feedback"
      data-feedback={feedback.kind}
      inert={suppressed ? true : undefined}
      role="status"
    >
      <Icon aria-hidden="true" className="activity-host__feedback-icon" />
      <p>{getFeedbackMessage(feedback)}</p>
      {feedback.kind === "retry" ? (
        <PrimaryCommandButton
          data-activity-control="retry"
          disabled={suppressed}
          icon={RotateCcw}
          onClick={onRetry}
        >
          再试一次
        </PrimaryCommandButton>
      ) : null}
    </div>
  );
}

interface ActivityOverlayProps<TAction, TRenderModel> {
  readonly controller: ActivityHostController<TAction, TRenderModel>;
  readonly idPrefix: string;
  readonly instructionTitle: string;
  readonly onExit?: () => void;
  readonly onReplay: MouseEventHandler<HTMLButtonElement>;
  readonly onResume: MouseEventHandler<HTMLButtonElement>;
  readonly onRetryLevel: MouseEventHandler<HTMLButtonElement>;
  readonly onStart: MouseEventHandler<HTMLButtonElement>;
}

function ActivityOverlay<TAction, TRenderModel>({
  controller,
  idPrefix,
  instructionTitle,
  onExit,
  onReplay,
  onResume,
  onRetryLevel,
  onStart
}: ActivityOverlayProps<TAction, TRenderModel>) {
  const tutorialTitleId = idPrefix + "-tutorial-title";
  const pauseTitleId = idPrefix + "-pause-title";
  const resultTitleId = idPrefix + "-result-title";
  const errorTitleId = idPrefix + "-error-title";

  switch (controller.phase) {
    case "loading":
      return (
        <div className="activity-host__overlay">
          <p aria-live="polite" role="status">
            点点正在摆好这一关
          </p>
        </div>
      );
    case "replaying":
      return (
        <div className="activity-host__overlay">
          <RotateCcw aria-hidden="true" className="activity-host__state-icon" />
          <p aria-live="polite" role="status">
            小关卡重新摆好啦
          </p>
        </div>
      );
    case "tutorial":
      return (
        <section
          aria-labelledby={tutorialTitleId}
          className="activity-host__overlay activity-host__overlay--tutorial"
        >
          <BookOpen aria-hidden="true" className="activity-host__state-icon" />
          <h2 id={tutorialTitleId}>{instructionTitle}</h2>
          <PrimaryCommandButton
            data-activity-control="start"
            icon={Play}
            onClick={onStart}
          >
            我准备好了
          </PrimaryCommandButton>
        </section>
      );
    case "paused":
      return (
        <section
          aria-labelledby={pauseTitleId}
          aria-modal="true"
          className="activity-host__overlay"
          role="dialog"
          tabIndex={-1}
        >
          <Pause aria-hidden="true" className="activity-host__state-icon" />
          <h2 id={pauseTitleId}>休息一下</h2>
          <div className="activity-host__pause-actions">
            <PrimaryCommandButton
              data-activity-control="resume"
              icon={Play}
              onClick={onResume}
            >
              继续活动
            </PrimaryCommandButton>
            {onExit ? (
              <IconButton
                data-activity-control="pause-exit"
                icon={ArrowLeft}
                label="返回地图"
                onClick={onExit}
              />
            ) : null}
          </div>
        </section>
      );
    case "result":
      return (
        <section
          aria-labelledby={resultTitleId}
          className="activity-host__overlay activity-host__overlay--result"
        >
          <CheckCircle2
            aria-hidden="true"
            className="activity-host__state-icon"
          />
          <div aria-live="polite" role="status">
            <h2 id={resultTitleId}>这一关走到终点啦</h2>
            <p>
              答对了 {formatCorrectRate(controller.metrics?.correctRate ?? 0)}
            </p>
          </div>
          <PrimaryCommandButton
            data-activity-control="replay"
            icon={RotateCcw}
            onClick={onReplay}
          >
            再玩一次
          </PrimaryCommandButton>
        </section>
      );
    case "level-error":
      return (
        <section
          aria-labelledby={errorTitleId}
          className="activity-host__overlay activity-host__overlay--error"
        >
          <CircleHelp aria-hidden="true" className="activity-host__state-icon" />
          <h2 id={errorTitleId}>小关卡停了一下</h2>
          <p aria-live="assertive" role="alert">
            {controller.error?.message}
          </p>
          <div className="activity-host__error-actions">
            {controller.error?.recoverable ? (
              <PrimaryCommandButton
                data-activity-control="retry-level"
                icon={RotateCcw}
                onClick={onRetryLevel}
              >
                重新加载
              </PrimaryCommandButton>
            ) : null}
            {onExit ? (
              <PrimaryCommandButton icon={ArrowLeft} onClick={onExit}>
                返回地图
              </PrimaryCommandButton>
            ) : null}
          </div>
        </section>
      );
    case "playing":
      return null;
  }
}

function ActivityHostSession<
  TPayload,
  TState,
  TAction,
  TRenderModel
>({
  actions,
  adapter,
  bridge,
  instruction,
  levelId,
  levelTitle,
  nowMs,
  onComplete,
  onExit,
  onLevelError,
  payload,
  reducedMotion,
  renderActivity
}: ActivityHostProps<TPayload, TState, TAction, TRenderModel>) {
  const controller = useActivityHost({
    actions,
    adapter,
    bridge,
    instruction,
    levelId,
    nowMs,
    onComplete,
    onLevelError,
    payload,
    reducedMotion
  });
  const gameContentRef = useRef<HTMLDivElement>(null);
  const focusGameAfterReplayRef = useRef(false);
  const pauseTriggerRef = useRef<HTMLButtonElement | null>(null);
  const rootRef = useRef<HTMLElement>(null);
  const previousPhase = useRef(controller.phase);
  const idPrefix = useId();
  const titleId = idPrefix + "-title";
  const pauseDialogOpen = controller.phase === "paused";

  const onStart = useAccessibleActivation<HTMLButtonElement>(controller.start);
  const onPauseActivation =
    useAccessibleActivation<HTMLButtonElement>(controller.pause);
  const onResume = useAccessibleActivation<HTMLButtonElement>(controller.resume);
  const onHint =
    useAccessibleActivation<HTMLButtonElement>(controller.requestHint);
  const onRetry = useAccessibleActivation<HTMLButtonElement>(controller.retry);
  const replayActivity = controller.replay;
  const handleReplay = useCallback(
    (inputMethod: InputMethod) => {
      focusGameAfterReplayRef.current = true;
      replayActivity(inputMethod);
    },
    [replayActivity]
  );
  const onReplay = useAccessibleActivation<HTMLButtonElement>(handleReplay);
  const onReplayNarration =
    useAccessibleActivation<HTMLButtonElement>(controller.replayNarration);
  const onRetryLevel =
    useAccessibleActivation<HTMLButtonElement>(controller.retryLevel);
  const handlePause = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      pauseTriggerRef.current = event.currentTarget;
      onPauseActivation(event);
    },
    [onPauseActivation]
  );
  const focusedPhase = controller.phase;
  const recoverableError = controller.error?.recoverable;
  const resumeActivity = controller.resume;

  useEffect(() => {
    let removePauseKeyListener: (() => void) | undefined;
    let control: string | null = null;
    let focusTarget: HTMLElement | null = null;

    switch (focusedPhase) {
      case "tutorial":
        control = "start";
        break;
      case "paused": {
        const dialog =
          rootRef.current?.querySelector<HTMLElement>(
            '[role="dialog"][aria-modal="true"]'
          ) ?? null;
        focusTarget =
          dialog?.querySelector<HTMLButtonElement>(
            '[data-activity-control="resume"]'
          ) ?? dialog;

        const handlePauseKeyDown = (event: KeyboardEvent) => {
          const currentDialog =
            rootRef.current?.querySelector<HTMLElement>(
              '[role="dialog"][aria-modal="true"]'
            ) ?? null;

          if (currentDialog === null) {
            return;
          }

          if (event.key === "Escape") {
            event.preventDefault();
            resumeActivity("keyboard");
            return;
          }

          if (event.key !== "Tab") {
            return;
          }

          const controls = Array.from(
            currentDialog.querySelectorAll<HTMLButtonElement>(
              "button:not([disabled])"
            )
          ).filter(
            (element) =>
              !element.hidden && element.getAttribute("aria-hidden") !== "true"
          );
          const firstControl = controls[0];
          const lastControl = controls.at(-1);

          if (!firstControl || !lastControl) {
            event.preventDefault();
            currentDialog.focus();
            return;
          }

          if (!currentDialog.contains(document.activeElement)) {
            event.preventDefault();
            (event.shiftKey ? lastControl : firstControl).focus();
            return;
          }

          if (event.shiftKey && document.activeElement === firstControl) {
            event.preventDefault();
            lastControl.focus();
          } else if (
            !event.shiftKey &&
            document.activeElement === lastControl
          ) {
            event.preventDefault();
            firstControl.focus();
          }
        };

        document.addEventListener("keydown", handlePauseKeyDown);
        removePauseKeyListener = () => {
          document.removeEventListener("keydown", handlePauseKeyDown);
        };
        break;
      }
      case "result":
        control = "replay";
        break;
      case "level-error":
        focusGameAfterReplayRef.current = false;
        control = recoverableError ? "retry-level" : "exit";
        break;
      case "playing":
        if (focusGameAfterReplayRef.current) {
          focusGameAfterReplayRef.current = false;
          focusTarget = gameContentRef.current;
        } else if (previousPhase.current === "paused") {
          const pauseTrigger = pauseTriggerRef.current;

          if (
            pauseTrigger?.isConnected &&
            rootRef.current?.contains(pauseTrigger)
          ) {
            focusTarget = pauseTrigger;
          } else {
            control = "pause";
          }
        }
        break;
      case "loading":
      case "replaying":
        break;
    }

    if (focusTarget === null && control !== null) {
      focusTarget =
        rootRef.current?.querySelector<HTMLButtonElement>(
          '[data-activity-control="' + control + '"]'
        ) ?? null;
    }

    focusTarget?.focus();

    previousPhase.current = focusedPhase;

    return removePauseKeyListener;
  }, [focusedPhase, recoverableError, resumeActivity]);

  const renderContext = useMemo<
    ActivityHostRenderContext<TAction, TRenderModel> | null
  >(
    () =>
      controller.activity === null
        ? null
        : {
            dispatch: controller.dispatch,
            getActivationProps: controller.getActivationProps,
            isPaused: controller.phase === "paused",
            model: controller.activity.model,
            reducedMotion: controller.reducedMotion
          },
    [controller]
  );
  const activityIsInteractive = controller.phase === "playing";
  const activityControlsMounted = activityIsInteractive || pauseDialogOpen;
  const instructionTitle = instruction.title?.trim() || "先听点点说";
  const narrationDisabled =
    controller.phase === "loading" ||
    controller.phase === "replaying" ||
    controller.phase === "level-error" ||
    pauseDialogOpen;

  const handleExit = useCallback(() => onExit?.(), [onExit]);

  return (
    <section
      aria-labelledby={titleId}
      className="activity-host"
      data-phase={controller.phase}
      data-reduced-motion={controller.reducedMotion}
      ref={rootRef}
    >
      <header className="activity-host__header">
        <h1 id={titleId}>{levelTitle}</h1>
        <div
          aria-hidden={pauseDialogOpen || undefined}
          aria-label="活动控制"
          className="activity-host__toolbar"
          inert={pauseDialogOpen ? true : undefined}
          role="toolbar"
        >
          <AudioReplayButton
            data-activity-control="replay-narration"
            disabled={narrationDisabled}
            onClick={onReplayNarration}
          />
          {activityControlsMounted ? (
            <>
              <IconButton
                data-activity-control="hint"
                disabled={pauseDialogOpen}
                icon={Lightbulb}
                label="给我提示"
                onClick={onHint}
                tooltip="给我提示"
              />
              <IconButton
                data-activity-control="pause"
                disabled={pauseDialogOpen}
                icon={Pause}
                label="暂停活动"
                onClick={handlePause}
                tooltip="暂停活动"
              />
            </>
          ) : null}
          {onExit ? (
            <IconButton
              data-activity-control="exit"
              disabled={pauseDialogOpen}
              icon={ArrowLeft}
              label="返回地图"
              onClick={handleExit}
              tooltip="返回地图"
            />
          ) : null}
        </div>
      </header>

      <div
        aria-busy={
          controller.phase === "loading" ||
          controller.phase === "replaying" ||
          undefined
        }
        className="activity-host__game-frame"
      >
        <div
          aria-label="游戏主区域"
          aria-hidden={!activityIsInteractive || undefined}
          className="activity-host__game-content"
          inert={!activityIsInteractive ? true : undefined}
          ref={gameContentRef}
          role="group"
          tabIndex={-1}
        >
          {renderContext ? (
            <ActivityRenderBoundary
              key={controller.rendererLifecycle}
              onError={controller.failRender}
            >
              <ActivityRenderer
                context={renderContext}
                renderActivity={renderActivity}
              />
            </ActivityRenderBoundary>
          ) : null}
        </div>
        <ActivityOverlay
          controller={controller}
          idPrefix={idPrefix}
          instructionTitle={instructionTitle}
          onExit={onExit ? handleExit : undefined}
          onReplay={onReplay}
          onResume={onResume}
          onRetryLevel={onRetryLevel}
          onStart={onStart}
        />
      </div>

      <div
        aria-hidden={pauseDialogOpen || undefined}
        className="activity-host__caption"
        inert={pauseDialogOpen ? true : undefined}
      >
        <LiveCaption
          caption={controller.caption}
          fallbackText={instruction.caption}
        />
      </div>
      <FeedbackNotice
        feedback={controller.feedback}
        onRetry={onRetry}
        suppressed={pauseDialogOpen}
      />
    </section>
  );
}

export function ActivityHost<
  TPayload,
  TState,
  TAction,
  TRenderModel
>(props: ActivityHostProps<TPayload, TState, TAction, TRenderModel>) {
  const sessionKey = props.levelId + ":" + props.adapter.activityType;

  return <ActivityHostSession {...props} key={sessionKey} />;
}
